import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  X, ChevronLeft, ChevronRight, Download, Share2, RefreshCw,
  ThumbsUp, ThumbsDown, MessageCircle, ZoomIn, ZoomOut, User,
  Shirt, Palette, Check, Loader2, Copy,
} from 'lucide-react';

const RATING_OPTIONS = [
  { value: 'excellent', label: 'Excellent', color: '#16A34A' },
  { value: 'good', label: 'Good', color: '#059669' },
  { value: 'acceptable', label: 'Acceptable', color: '#D97706' },
  { value: 'poor', label: 'Poor', color: '#EA580C' },
  { value: 'rejected', label: 'Rejected', color: '#DC2626' },
];

export function TryOnImageLightbox({
  images = [],
  initialIndex = 0,
  isOpen,
  onClose,
  onApprove,
  onReject,
  onRegenerate,
  onRate,
  showComparison = false,
}) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const currentImage = images[currentIndex];

  // Reset state when opening
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      setZoom(1);
    }
  }, [isOpen, initialIndex]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          navigatePrev();
          break;
        case 'ArrowRight':
          navigateNext();
          break;
        case '+':
        case '=':
          setZoom(z => Math.min(z + 0.25, 3));
          break;
        case '-':
          setZoom(z => Math.max(z - 0.25, 0.5));
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, images.length]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const navigatePrev = useCallback(() => {
    setCurrentIndex(i => (i > 0 ? i - 1 : images.length - 1));
    setZoom(1);
  }, [images.length]);

  const navigateNext = useCallback(() => {
    setCurrentIndex(i => (i < images.length - 1 ? i + 1 : 0));
    setZoom(1);
  }, [images.length]);

  const handleCopyImage = async () => {
    if (!currentImage?.generated_image_url) return;
    try {
      setActionLoading('copy');
      if (currentImage.generated_image_url.startsWith('data:')) {
        const res = await fetch(currentImage.generated_image_url);
        const blob = await res.blob();
        await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
        toast.success('Image copied to clipboard');
      } else {
        toast.error('Cannot copy this image format');
      }
    } catch (err) {
      toast.error('Copy failed — try downloading instead');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownload = async () => {
    if (!currentImage?.generated_image_url) return;
    
    try {
      setActionLoading('download');
      
      // For base64 data URLs, create a blob and download
      if (currentImage.generated_image_url.startsWith('data:')) {
        const response = await fetch(currentImage.generated_image_url);
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `tryon-${currentImage.client_name || 'image'}-${currentImage.garment_type || 'look'}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        // For regular URLs
        const a = document.createElement('a');
        a.href = currentImage.generated_image_url;
        a.download = `tryon-${currentImage.id}.png`;
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      
      toast.success('Image downloaded');
    } catch (err) {
      toast.error('Failed to download image');
    } finally {
      setActionLoading(null);
    }
  };

  const handleShare = async () => {
    if (!currentImage?.generated_image_url) return;
    
    try {
      setActionLoading('share');
      
      const clientName = currentImage.client_name || 'Valued Client';
      const garment = currentImage.garment_type?.replace(/_/g, ' ') || 'garment';
      const fabric = currentImage.fabric_description || 'premium fabric';
      
      const message = `Hi ${clientName}! Here's a preview of your ${garment} in ${fabric}. What do you think? - Herringbone & Sui`;
      
      // Try to use Web Share API if available
      if (navigator.share && navigator.canShare) {
        // Convert data URL to blob for sharing
        if (currentImage.generated_image_url.startsWith('data:')) {
          const response = await fetch(currentImage.generated_image_url);
          const blob = await response.blob();
          const file = new File([blob], 'tryon.png', { type: 'image/png' });
          
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              title: 'Try-On Preview',
              text: message,
              files: [file],
            });
            toast.success('Shared successfully');
            return;
          }
        }
      }
      
      // Fallback: Copy message and open WhatsApp
      await navigator.clipboard.writeText(message);
      
      // Open WhatsApp with pre-filled message
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
      
      toast.success('Message copied! Download the image to attach in WhatsApp.');
      
      // Also trigger download for convenience
      handleDownload();
      
    } catch (err) {
      toast.error('Failed to share');
    } finally {
      setActionLoading(null);
    }
  };

  const handleApprove = async () => {
    if (!onApprove || !currentImage) return;
    setActionLoading('approve');
    try {
      await onApprove(currentImage.id);
      toast.success('Image approved');
    } catch (err) {
      toast.error('Failed to approve');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!onReject || !currentImage) return;
    setActionLoading('reject');
    try {
      await onReject(currentImage.id);
      toast.success('Image rejected');
    } catch (err) {
      toast.error('Failed to reject');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRegenerate = async () => {
    if (!onRegenerate || !currentImage) return;
    setActionLoading('regenerate');
    try {
      await onRegenerate(currentImage);
      toast.success('Regeneration started');
    } catch (err) {
      toast.error('Failed to start regeneration');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRate = async (rating) => {
    if (!onRate || !currentImage) return;
    setActionLoading(`rate-${rating}`);
    try {
      await onRate(currentImage.id, rating);
      toast.success(`Rated as ${rating}`);
    } catch (err) {
      toast.error('Failed to rate');
    } finally {
      setActionLoading(null);
    }
  };

  if (!isOpen || !currentImage) return null;

  const content = (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.95)' }}
      onClick={onClose}
      data-testid="tryon-lightbox"
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        data-testid="lightbox-close"
      >
        <X className="w-6 h-6 text-white" />
      </button>

      {/* Navigation arrows */}
      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); navigatePrev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="w-8 h-8 text-white" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); navigateNext(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            data-testid="lightbox-next"
          >
            <ChevronRight className="w-8 h-8 text-white" />
          </button>
        </>
      )}

      {/* Image counter */}
      {images.length > 1 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 px-4 py-2 rounded-full bg-white/10 text-white text-sm">
          {currentIndex + 1} / {images.length}
        </div>
      )}

      {/* Zoom controls */}
      <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
        <button
          onClick={(e) => { e.stopPropagation(); setZoom(z => Math.max(z - 0.25, 0.5)); }}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          disabled={zoom <= 0.5}
        >
          <ZoomOut className="w-5 h-5 text-white" />
        </button>
        <span className="text-white text-sm min-w-[50px] text-center">{Math.round(zoom * 100)}%</span>
        <button
          onClick={(e) => { e.stopPropagation(); setZoom(z => Math.min(z + 0.25, 3)); }}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          disabled={zoom >= 3}
        >
          <ZoomIn className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Main content area */}
      <div 
        className="flex flex-col items-center max-h-[90vh] w-full max-w-5xl px-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Image container */}
        <div className="relative flex-1 flex items-center justify-center overflow-hidden w-full mb-4">
          {showComparison && currentImage.source_photo_url ? (
            // Side-by-side comparison view
            <div className="flex gap-4 items-center justify-center">
              <div className="flex flex-col items-center">
                <img
                  src={currentImage.source_photo_url}
                  alt="Original"
                  className="max-h-[60vh] w-auto object-contain rounded-lg"
                  style={{ transform: `scale(${zoom})`, transition: 'transform 0.2s' }}
                />
                <span className="text-white/60 text-xs mt-2">Original Photo</span>
              </div>
              <div className="flex flex-col items-center">
                <img
                  src={currentImage.generated_image_url}
                  alt="Try-on result"
                  className="max-h-[60vh] w-auto object-contain rounded-lg"
                  style={{ transform: `scale(${zoom})`, transition: 'transform 0.2s' }}
                />
                <span className="text-white/60 text-xs mt-2">Generated Look</span>
              </div>
            </div>
          ) : (
            // Single image view
            <img
              src={currentImage.generated_image_url}
              alt="Try-on result"
              className="max-h-[65vh] w-auto object-contain rounded-lg"
              style={{ transform: `scale(${zoom})`, transition: 'transform 0.2s', touchAction: 'pinch-zoom' }}
              data-testid="lightbox-image"
            />
          )}
        </div>

        {/* Image info */}
        <div className="w-full max-w-2xl text-center mb-4">
          <div className="flex items-center justify-center gap-4 flex-wrap">
            {currentImage.client_name && (
              <div className="flex items-center gap-1.5 text-white/80">
                <User className="w-4 h-4" />
                <span className="text-sm">{currentImage.client_name}</span>
              </div>
            )}
            {currentImage.garment_type && (
              <div className="flex items-center gap-1.5 text-white/80">
                <Shirt className="w-4 h-4" />
                <span className="text-sm capitalize">{currentImage.garment_type.replace(/_/g, ' ')}</span>
              </div>
            )}
            {currentImage.fabric_code && (
              <div className="flex items-center gap-1.5 text-white/80">
                <Palette className="w-4 h-4" />
                <span className="text-sm">{currentImage.fabric_code}</span>
              </div>
            )}
          </div>
          {currentImage.fabric_description && (
            <p className="text-white/60 text-xs mt-2 line-clamp-2">{currentImage.fabric_description}</p>
          )}
          {currentImage.quality_rating && (
            <Badge className="mt-2" style={{
              backgroundColor: RATING_OPTIONS.find(r => r.value === currentImage.quality_rating)?.color || '#6B7280',
              color: 'white',
            }}>
              {currentImage.quality_rating}
            </Badge>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap items-center justify-center gap-3 pb-4">
          {/* Approve/Reject */}
          {onApprove && (
            <Button
              onClick={handleApprove}
              disabled={actionLoading === 'approve'}
              className="gap-2"
              style={{ backgroundColor: '#16A34A' }}
            >
              {actionLoading === 'approve' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsUp className="w-4 h-4" />}
              Approve
            </Button>
          )}
          {onReject && (
            <Button
              onClick={handleReject}
              disabled={actionLoading === 'reject'}
              variant="destructive"
              className="gap-2"
            >
              {actionLoading === 'reject' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsDown className="w-4 h-4" />}
              Reject
            </Button>
          )}

          {/* Share, Copy & Download */}
          <Button
            onClick={handleShare}
            disabled={actionLoading === 'share'}
            variant="outline"
            className="gap-2 border-white/30 text-white hover:bg-white/10"
            data-testid="lightbox-whatsapp-btn"
          >
            {actionLoading === 'share' ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
            Send via WhatsApp
          </Button>
          <Button
            onClick={handleCopyImage}
            disabled={actionLoading === 'copy'}
            variant="outline"
            className="gap-2 border-white/30 text-white hover:bg-white/10"
            data-testid="lightbox-copy-btn"
          >
            {actionLoading === 'copy' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
            Copy Image
          </Button>
          <Button
            onClick={handleDownload}
            disabled={actionLoading === 'download'}
            variant="outline"
            className="gap-2 border-white/30 text-white hover:bg-white/10"
          >
            {actionLoading === 'download' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Download
          </Button>

          {/* Regenerate */}
          {onRegenerate && (
            <Button
              onClick={handleRegenerate}
              disabled={actionLoading === 'regenerate'}
              variant="outline"
              className="gap-2 border-white/30 text-white hover:bg-white/10"
            >
              {actionLoading === 'regenerate' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Regenerate
            </Button>
          )}
        </div>

        {/* Rating buttons (if no approve/reject) */}
        {onRate && !onApprove && (
          <div className="flex flex-wrap items-center justify-center gap-2 pb-4">
            <span className="text-white/60 text-xs mr-2">Rate:</span>
            {RATING_OPTIONS.map(opt => (
              <Button
                key={opt.value}
                onClick={() => handleRate(opt.value)}
                disabled={actionLoading?.startsWith('rate-')}
                variant="outline"
                size="sm"
                className="text-xs px-3"
                style={{ 
                  borderColor: opt.color, 
                  color: currentImage.quality_rating === opt.value ? 'white' : opt.color,
                  backgroundColor: currentImage.quality_rating === opt.value ? opt.color : 'transparent',
                }}
              >
                {actionLoading === `rate-${opt.value}` ? <Loader2 className="w-3 h-3 animate-spin" /> : opt.label}
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

export default TryOnImageLightbox;
